import React, { FC, useCallback, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApolloClient } from '@apollo/client';
import { useToaster } from '@/components/core/Toaster';
import {
  INVOKE_STAFF_ATTRIBUTION_BACKFILL,
  INVOKE_STAFF_ATTRIBUTION_COORDINATOR,
  InvokeStaffAttributionBackfillData,
  InvokeStaffAttributionBackfillVars,
  InvokeStaffAttributionCoordinatorData,
  InvokeStaffAttributionCoordinatorVars,
  StaffAttributionBackfillMode,
} from '@/graph/staff-attribution-admin';

/**
 * Super-admin dialog for triggering the staff-attribution rollup engine
 * (coordinator + backfill Lambdas). Lives on the StaffMonitor page —
 * topical neighbor for "staff-related admin actions" — and is gated by
 * `useDebugMode().canBypassPermissions` (super-admin + debug mode) at the
 * call site so the cog icon never surfaces on non-admin shells.
 *
 * Note on placement: StaffMonitor is *not* a consumer of the rollup
 * output (its `calculateCost` column reads beacon-time directly, not the
 * `staffAttribution` table). The dialog lives here because it's the
 * closest super-admin-appropriate surface for staff-related actions —
 * the cost popup (which is the actual consumer) is per-procedure and
 * org-wide rollup triggers don't belong inside it.
 *
 * The `staffAttributionRollupRuns` run-status read API is deferred
 * (IMPLEMENTATION_GUIDE.md Stream 3), so this dialog reports outcomes
 * from the immediate `BackfillInvokeResult` envelope only — no polling.
 */
interface StaffAttributionAdminDialogProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  // Form rows: a `Box` flex container with consistent inter-control gap.
  // `theme.spacing(2)` = 16px, matching the original inline value.
  controlsRow: {
    gap: theme.spacing(2),
  },
  // Spinner that sits at the leading edge of an action button.
  buttonProgress: {
    marginRight: theme.spacing(1),
  },
  // Backfill description sits a little farther from its controls row to
  // give the multi-line copy room to breathe; matches the prior 20px.
  backfillDescription: {
    marginBottom: theme.spacing(2.5),
  },
  daysFormControl: {
    minWidth: 100,
  },
  daysInput: {
    width: 90,
  },
  modeFormControl: {
    minWidth: 130,
  },
  targetDateField: {
    width: 170,
  },
}));

const StaffAttributionAdminDialog: FC<StaffAttributionAdminDialogProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const client = useApolloClient();
  const toaster = useToaster();

  const [invokingCoordinator, setInvokingCoordinator] = useState(false);
  const [coordinatorDryRun, setCoordinatorDryRun] = useState(false);

  const [invokingBackfill, setInvokingBackfill] = useState(false);
  const [backfillDays, setBackfillDays] = useState(30);
  const [backfillMode, setBackfillMode] = useState<StaffAttributionBackfillMode>('missing_only');
  const [backfillTargetDate, setBackfillTargetDate] = useState<string>('');
  const [backfillDryRun, setBackfillDryRun] = useState(false);

  const handleInvokeCoordinator = useCallback(async () => {
    setInvokingCoordinator(true);
    try {
      const { data } = await client.mutate<
        InvokeStaffAttributionCoordinatorData,
        InvokeStaffAttributionCoordinatorVars
      >({
        mutation: INVOKE_STAFF_ATTRIBUTION_COORDINATOR,
        variables: { dryRun: coordinatorDryRun },
      });
      const result = data?.invokeStaffAttributionCoordinator;
      if (result?.success) {
        toaster.success(`Coordinator invoked${coordinatorDryRun ? ' (dry run)' : ''}`);
      } else {
        toaster.error(`Coordinator failed: ${result?.message ?? 'Unknown error'}`);
      }
    } catch (err: any) {
      toaster.error(`Coordinator invocation failed: ${err.message}`);
    } finally {
      setInvokingCoordinator(false);
    }
  }, [client, toaster, coordinatorDryRun]);

  const handleInvokeBackfill = useCallback(async () => {
    setInvokingBackfill(true);
    try {
      const { data } = await client.mutate<InvokeStaffAttributionBackfillData, InvokeStaffAttributionBackfillVars>({
        mutation: INVOKE_STAFF_ATTRIBUTION_BACKFILL,
        variables: {
          backfillDays,
          backfillMode,
          targetDate: backfillTargetDate.trim() === '' ? null : backfillTargetDate,
          dryRun: backfillDryRun,
        },
      });
      const result = data?.invokeStaffAttributionBackfill;
      if (result?.success) {
        toaster.success(`Backfill invoked${backfillDryRun ? ' (dry run)' : ''}`);
      } else {
        toaster.error(`Backfill failed: ${result?.message ?? 'Unknown error'}`);
      }
    } catch (err: any) {
      toaster.error(`Backfill invocation failed: ${err.message}`);
    } finally {
      setInvokingBackfill(false);
    }
  }, [client, toaster, backfillDays, backfillMode, backfillTargetDate, backfillDryRun]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Staff Attribution — Admin</DialogTitle>
      <DialogContent>
        {/* Coordinator */}
        <Box py={1}>
          <Typography variant="subtitle2">Rollup Coordinator</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Invoke the coordinator Lambda to materialize attribution rows for orgs whose eligibility window is open.
            Runs for yesterday (org TZ) plus the 7-day healing window.
          </Typography>
          <Box display="flex" alignItems="center" className={classes.controlsRow} mb={1.5} flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  checked={coordinatorDryRun}
                  onChange={e => setCoordinatorDryRun(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Dry run</Typography>}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleInvokeCoordinator}
            disabled={invokingCoordinator}
          >
            {invokingCoordinator ? <CircularProgress size={16} className={classes.buttonProgress} /> : null}
            Invoke Coordinator
          </Button>
        </Box>

        <Divider />

        {/* Backfill */}
        <Box py={1}>
          <Typography variant="subtitle2">Rollup Backfill</Typography>
          <Typography variant="body2" color="textSecondary" className={classes.backfillDescription}>
            Re-run attribution for a window ending at <em>Target date</em> (or yesterday in org TZ when empty), spanning{' '}
            <em>Days</em> backwards. <em>Missing only</em> skips dates already marked successful; <em>Overwrite all</em>{' '}
            re-runs every date in the window (the SP still preserves manual edits via per-staff diff; force-replace is
            not used).
          </Typography>
          {/* flex-start so the date picker's `helperText` (rendered below
              the input) does not pull the sibling controls downward, as
              `alignItems: center` would. Each control anchors at the top
              of the row regardless of whether it has helper text. */}
          <Box display="flex" alignItems="flex-start" className={classes.controlsRow} mb={1.5} flexWrap="wrap">
            <FormControl size="small" className={classes.daysFormControl}>
              <TextField
                label="Days"
                type="number"
                size="small"
                variant="outlined"
                value={backfillDays}
                onChange={e => setBackfillDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 365 }}
                className={classes.daysInput}
              />
            </FormControl>
            <FormControl size="small" variant="outlined" className={classes.modeFormControl}>
              <Select
                value={backfillMode}
                onChange={e => setBackfillMode(e.target.value as StaffAttributionBackfillMode)}
              >
                <MenuItem value="missing_only">Missing only</MenuItem>
                <MenuItem value="overwrite_all">Overwrite all</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Target date"
              type="date"
              size="small"
              variant="outlined"
              value={backfillTargetDate}
              onChange={e => setBackfillTargetDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              className={classes.targetDateField}
              helperText="Leave empty for yesterday"
            />
          </Box>
          {/* Dry run on its own row so it doesn't have to share vertical
              alignment with the form-control row above (the date
              picker's helperText pulls flex centers around). */}
          <Box mb={1.5}>
            <FormControlLabel
              control={
                <Checkbox checked={backfillDryRun} onChange={e => setBackfillDryRun(e.target.checked)} size="small" />
              }
              label={<Typography variant="body2">Dry run</Typography>}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleInvokeBackfill}
            disabled={invokingBackfill}
          >
            {invokingBackfill ? <CircularProgress size={16} className={classes.buttonProgress} /> : null}
            Invoke Backfill
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StaffAttributionAdminDialog;
